import { ErrorHandler, Injectable } from '@angular/core';
import { ErrorNotificationService } from '../services/error-notification.service';
import { GlobalService } from '../services/global.service';

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  constructor(
    private errorNotificationService: ErrorNotificationService,
    private globalService: GlobalService,
  ) {}

  handleError(error: any): void {
    const rawError = error?.rejection || error;
    const errorMessage = rawError?.message
      ? String(rawError.message)
      : typeof rawError === 'string'
        ? rawError
        : rawError?.toString && rawError.toString() !== '[object Object]'
          ? String(rawError.toString())
          : '';
    const errorName = rawError?.name || error?.name || '';
    const status = Number(rawError?.status ?? error?.status ?? rawError?.statusCode ?? error?.statusCode ?? 0);
    const lowerMessage = errorMessage.toLowerCase();

    // 🛡️ Ignorar erros inofensivos de teardown assíncrono (RxJS ObjectUnsubscribedError / EmptyError / AbortError / HTTP status 0 cancelado)
    // e respostas HTTP (HttpErrorResponse, 4xx/5xx ou strings como Forbidden / Unauthorized / Unexpected / Offline) já tratadas pelos interceptors/serviços
    if (
      errorName === 'ObjectUnsubscribedError' ||
      errorName === 'EmptyError' ||
      errorName === 'AbortError' ||
      errorName === 'HttpErrorResponse' ||
      typeof rawError?.status === 'number' ||
      typeof error?.status === 'number' ||
      typeof rawError?.statusCode === 'number' ||
      typeof error?.statusCode === 'number' ||
      status === 401 ||
      status === 403 ||
      lowerMessage.includes('object unsubscribed') ||
      lowerMessage.includes('objectunsubscribederror') ||
      lowerMessage.includes('no elements in sequence') ||
      lowerMessage.includes('http failure response') ||
      lowerMessage === 'forbidden' ||
      lowerMessage.includes('forbidden') ||
      lowerMessage === 'unauthorized' ||
      lowerMessage.includes('unauthorized') ||
      lowerMessage.includes('unexpected error') ||
      lowerMessage.includes('não foi possível concluir a operação') ||
      lowerMessage.includes('se produjo un error inesperado') ||
      lowerMessage.includes('une erreur inattendue') ||
      lowerMessage.includes('si è verificato un errore') ||
      lowerMessage.includes('net::err_aborted') ||
      lowerMessage.includes('failed to fetch') ||
      lowerMessage.includes('networkerror') ||
      errorMessage.includes('ERRORS.FORBIDDEN') ||
      errorMessage.includes('ERRORS.UNAUTHORIZED') ||
      errorMessage.includes('ERRORS.NO_INTERNET') ||
      errorMessage.includes('ERRORS.TIMEOUT') ||
      errorMessage.includes('ERRORS.NOT_FOUND') ||
      errorMessage.includes('ERRORS.UNEXPECTED') ||
      errorMessage.includes('ERRORS.BAD_REQUEST') ||
      errorMessage.includes('ERRORS.OFFLINE')
    ) {
      return;
    }

    // 🛡️ Não reportar erros em ambiente local (localhost / 127.0.0.1) ou sob headless/test automation (Playwright, HeadlessChrome, Prerender)
    if (typeof window !== 'undefined') {
      const hostname = window.location?.hostname || '';
      if (
        hostname === 'localhost' ||
        hostname === '127.0.0.1' ||
        hostname === '0.0.0.0' ||
        hostname.endsWith('.local')
      ) {
        return;
      }
      const ua = navigator?.userAgent || '';
      if (ua.includes('HeadlessChrome') || ua.includes('Playwright') || ua.includes('Prerender')) {
        return;
      }
    }

    // Ignorar eventos de DOM (ex: erro de carregamento de imagem/iframe) e objetos vazios ({}) sem stack/message
    if (
      typeof Event !== 'undefined' &&
      (rawError instanceof Event || error instanceof Event) &&
      !(rawError as any)?.error
    ) {
      return;
    }
    if (
      rawError &&
      typeof rawError === 'object' &&
      !(rawError instanceof Error) &&
      !rawError.message &&
      !rawError.stack &&
      !rawError.status &&
      Object.keys(rawError).filter(k => k !== 'isTrusted').length === 0
    ) {
      return;
    }

    // Não bloqueia o fluxo de registos na consola para que o utilizador consiga debugar
    console.error('🚨 Global Error Handler Caught an Exception:', error);

    // Quando o utilizador clica num link lazy-loaded mas houve um deploy de uma nova versão (mudança de hash),
    // o Angular não consegue descarregar o ficheiro antigo e aborta silenciosamente a navegação (o link parece não funcionar).
    // A melhor experiência UX é forçar um refresh automático para obter a versão mais recente.
    if (errorMessage && (errorMessage.includes('Loading chunk') || errorMessage.includes('ChunkLoadError'))) {
      console.warn('⚠️ ChunkLoadError detetado! A recarregar a página para obter a versão mais recente...');
      window.location.reload();
      return;
    }

    // Normalizar o erro para garantir que tem sempre stack trace e mensagem legível em vez de [object Object]
    let errorToReport: any = rawError;
    if (typeof rawError === 'string') {
      errorToReport = new Error(rawError);
    } else if (rawError && typeof rawError === 'object' && !(rawError instanceof Error)) {
      const extractedMsg =
        errorMessage ||
        rawError.error ||
        rawError.message ||
        rawError.detail ||
        (rawError.toString && rawError.toString() !== '[object Object]' ? rawError.toString() : null) ||
        JSON.stringify(rawError);
      errorToReport = new Error(typeof extractedMsg === 'string' ? extractedMsg : JSON.stringify(extractedMsg));
      Object.assign(errorToReport, rawError);
    }

    this.errorNotificationService.notifyCriticalError(errorToReport, {
      component: 'GlobalErrorHandler',
      action: 'Unhandled Exception',
      message: errorMessage || errorToReport?.message || 'Erro no Frontend',
      userNIF: this.globalService?.loggedUserClass?.NIF || 'Desconhecido',
    });
  }
}
